import { usePersistedState } from "@/lib/persistedFilters";
// History tab — every generated / emailed / uploaded report, newest first,
// with filters (channel, status, destination, dates) and the SFTP transfer strip.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, HardDriveUpload, History, Loader2, RotateCcw, Server } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useDestinations } from "./DeliveryTarget";
import { fmtBytes } from "./reportsApi";

const TONE = {
  sent: "bg-emerald-50 text-emerald-700", paused: "bg-amber-50 text-amber-700",
  failed: "bg-rose-50 text-rose-700", empty: "bg-slate-100 text-slate-600",
};
const CHANNELS = [["", "All"], ["email", "Email"], ["sftp", "SFTP"]];
const STATUSES = [["", "Any status"], ["sent", "Sent"], ["failed", "Failed"], ["paused", "Paused"], ["empty", "No rows"]];

function SftpStrip({ refreshKey, onFailed }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/report-hub/sftp/stats").then((r) => setS(r.data)).catch(() => setS(null)); }, [refreshKey]);
  if (!s) return null;
  const tiles = [
    ["Destinations", s.destinations, Server, "", "sftp-stat-destinations"],
    ["Active SFTP schedules", s.active_schedules, CalendarClock, "", "sftp-stat-schedules"],
    ["Transfers", s.transfers, HardDriveUpload, "", "sftp-stat-transfers"],
    ["Failed (7 days)", s.failed_7d, AlertTriangle, s.failed_7d ? "border-rose-300 bg-rose-50" : "", "sftp-stat-failed"],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="sftp-stats">
      {tiles.map(([label, value, Icon, tone, testid]) => (
        <button key={label} type="button" onClick={label.startsWith("Failed") && value ? onFailed : undefined} data-testid={testid}
                className={`rounded-lg border px-3 py-2 text-left ${tone || "border-slate-200 bg-white"} ${label.startsWith("Failed") && value ? "cursor-pointer" : "cursor-default"}`}>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><Icon className="h-3 w-3" /> {label}</div>
          <div className={`text-lg font-semibold ${label.startsWith("Failed") && value ? "text-rose-700" : "text-slate-800"}`}>{value}</div>
          {label.startsWith("Failed") && s.last_failed && (
            <div className="truncate text-[11px] text-rose-700/80" title={s.last_failed.error}>last: {s.last_failed.name}</div>
          )}
          {label.startsWith("Active") && s.upcoming?.[0] && (
            <div className="truncate text-[11px] text-slate-400">next: {s.upcoming[0].name} · {new Date(s.upcoming[0].next_run_at).toLocaleString()}</div>
          )}
        </button>
      ))}
    </div>
  );
}

export function RunsTab({ refreshKey }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [sp] = useSearchParams();
  const [all, setAll] = useState(false);
  const [channel, setChannel] = usePersistedState("report-runs.channel", "", sp.get("channel"));
  const [status, setStatus] = usePersistedState("report-runs.status", "", sp.get("status"));
  const [destinationId, setDestinationId] = usePersistedState("report-runs.destination", "");
  const [dateFrom, setDateFrom] = usePersistedState("report-runs.from", "");
  const [dateTo, setDateTo] = usePersistedState("report-runs.to", "");
  const [rows, setRows] = useState(null);
  const [retrying, setRetrying] = useState("");
  const [tick, setTick] = useState(0);
  const destinations = useDestinations(true);

  useEffect(() => {
    setRows(null);
    api.get("/report-hub/runs", { params: { all, limit: 200, channel, status, destination_id: destinationId, date_from: dateFrom, date_to: dateTo } })
      .then((r) => setRows(r.data.runs || [])).catch(() => setRows([]));
  }, [all, refreshKey, tick, channel, status, destinationId, dateFrom, dateTo]);

  const retry = async (r) => {
    setRetrying(r.run_id);
    try {
      const { data } = await api.post(`/report-hub/runs/${r.run_id}/retry`);
      toast.success(data.status === "empty" ? "No rows — upload skipped" : `Uploaded to ${data.destination} — ${data.remote_path}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The retry failed too");
    } finally { setRetrying(""); setTick((t) => t + 1); }
  };

  const canRetry = (r) => r.channel === "sftp" && r.status === "failed" && r.retry_status !== "sent" && (isSuper || r.owner_user_id === user?.user_id);
  const sftpView = channel === "sftp";

  return (
    <div className="space-y-3" data-testid="runs-tab">
      <SftpStrip refreshKey={`${refreshKey}-${rows?.length ?? 0}`} onFailed={() => { setChannel("sftp"); setStatus("failed"); }} />
      <div className="flex flex-wrap items-center gap-2">
        {CHANNELS.map(([v, l]) => (
          <Button key={v || "all"} size="sm" variant={channel === v ? "default" : "outline"} onClick={() => setChannel(v)} data-testid={`runs-channel-${v || "all"}`}>{l}</Button>
        ))}
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="runs-status"
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {sftpView && (
          <select value={destinationId} onChange={(e) => setDestinationId(e.target.value)} data-testid="runs-destination"
                  className="h-8 max-w-[16rem] rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">All destinations</option>
            {(destinations || []).map((d) => <option key={d.destination_id} value={d.destination_id}>{d.name}</option>)}
          </select>
        )}
        <Input type="date" className="h-8 w-[10.5rem]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="runs-date-from" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" className="h-8 w-[10.5rem]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="runs-date-to" />
        {(channel || status || destinationId || dateFrom || dateTo) && (
          <Button size="sm" variant="ghost" onClick={() => { setChannel(""); setStatus(""); setDestinationId(""); setDateFrom(""); setDateTo(""); }} data-testid="runs-clear">Clear</Button>
        )}
        <span className="ml-auto text-xs text-slate-500" data-testid="runs-count">{rows ? `${rows.length} run${rows.length === 1 ? "" : "s"}` : ""}</span>
        {isSuper && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={all} onCheckedChange={setAll} data-testid="runs-show-all" /> Show everyone's
          </label>
        )}
      </div>
      {rows === null ? (
        <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed border-slate-300 p-10 text-center dark:border-slate-700 dark:bg-slate-900" data-testid="runs-empty">
          <History className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">{channel || status || destinationId || dateFrom || dateTo ? "No run matches those filters." : "Nothing has been emailed or uploaded yet."}</p>
        </Card>
      ) : (
        <Card className="overflow-x-auto border-slate-200 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-2">When</th><th className="px-4 py-2">Report</th><th className="px-4 py-2">Trigger</th>
                <th className="px-4 py-2">Format</th><th className="px-4 py-2 text-right">Rows</th>
                <th className="px-4 py-2">{sftpView ? "Destination · remote path" : "Delivered to"}</th>
                {sftpView && <th className="px-4 py-2 text-right">Size · time</th>}
                <th className="px-4 py-2">Status</th>{all && <th className="px-4 py-2">Owner</th>}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.run_id} className="border-t border-slate-50 dark:border-slate-800" data-testid={`run-row-${r.run_id}`}>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-600">{r.started_at ? new Date(r.started_at).toLocaleString() : ""}</td>
                  <td className="px-4 py-2 text-slate-800 dark:text-slate-100"><div className="font-medium">{r.name}</div><div className="text-xs text-slate-400">{r.report_label}</div></td>
                  <td className="px-4 py-2 capitalize text-slate-600">{r.trigger}</td>
                  <td className="px-4 py-2 uppercase text-slate-600">{r.format}</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-600">{r.rows ?? "—"}</td>
                  <td className="max-w-[300px] px-4 py-2 text-slate-600">
                    {r.channel === "sftp" ? (
                      <div data-testid="run-destination">
                        <div className="flex items-center gap-1 font-medium text-slate-700"><HardDriveUpload className="h-3 w-3" /> {r.destination_name || "—"}</div>
                        <div className="truncate font-mono text-[11px] text-slate-400" title={r.remote_path}>{r.remote_path || ""}</div>
                      </div>
                    ) : <span className="block truncate" title={(r.recipients || []).join(", ")}>{(r.recipients || []).join(", ")}</span>}
                  </td>
                  {sftpView && (
                    <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-[12px] text-slate-600">
                      {r.status === "sent" ? `${fmtBytes(r.bytes)} · ${r.duration_ms ?? 0} ms` : r.duration_ms ? `${r.duration_ms} ms` : "—"}
                    </td>
                  )}
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE[r.status] || TONE.empty}`} title={r.error || r.message || ""} data-testid="run-status">
                        {r.status}{r.error ? ` — ${r.error.slice(0, 60)}` : ""}
                      </span>
                      {r.retry_status && (
                        <span className={`text-[10px] ${r.retry_status === "sent" ? "text-emerald-700" : "text-rose-600"}`} data-testid="run-retry-state">
                          retried → {r.retry_status}
                        </span>
                      )}
                      {canRetry(r) && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => retry(r)} disabled={!!retrying}
                                data-testid={`run-retry-${r.run_id}`}>
                          {retrying === r.run_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Retry
                        </Button>
                      )}
                    </div>
                  </td>
                  {all && <td className="px-4 py-2 text-slate-600">{r.owner_name}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
